/**
 * ActionRulesEngine.ts
 *
 * Processes sync diffs (SyncChange[]) and generates ActionItems.
 * Pure client-side — only reads from Dexie, no API calls, no server.
 * The Pinia store (actionList.ts) handles all persistence to IndexedDB.
 */

import { isAttendeeStatus, isCancelledStatus } from '@/core/helpers/bookingStatus'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface SyncChange {
  table:  'calendar_bookings' | 'calendar_events' | 'resources_calendar'
  type:   'created' | 'updated' | 'deleted'
  before: Record<string, any> | null
  after:  Record<string, any>
}

export interface ActionOptions {
  priority:               1 | 2 | 3 | 4
  title:                  string
  description?:           string
  next_action?:           string
  status:                 'open' | 'done' | 'dismissed'
  related_id?:            string
  related_type?:          string
  confidence?:            number
  // E-Mail classification
  is_prospect?:           boolean  // true if classified as prospect/new-enquiry email
  // Event context for booking cancellations
  event_title?:           string
  event_start?:           string   // ISO timestamp of event start, for 4h outlook
  participants_remaining?: number

}

export interface GeneratedItem {
  type:    string
  options: ActionOptions
}

// ── Engine ────────────────────────────────────────────────────────────────────

export class ActionRulesEngine {
  constructor (private db: any) {}   // Dexie mycrogym_studio instance

  // ── Entry point ─────────────────────────────────────────────────────────────

  async processDiff (changes: SyncChange[]): Promise<GeneratedItem[]> {
    const items: GeneratedItem[] = []

    for (const change of changes) {
      try {
        items.push(...await this.applyRules(change))
      } catch (err) {
        console.warn('[ActionRulesEngine] Rule error:', change, err)
      }
    }

    // Deduplicate by related_id + topic
    const seen   = new Set<string>()
    return items.filter(i => {
      const key = `${i.options.related_id}::${i.type}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  // ── Dispatcher ───────────────────────────────────────────────────────────────

  private async applyRules (change: SyncChange): Promise<GeneratedItem[]> {
    if (change.table === 'calendar_bookings')  return this.ruleBooking(change)
    if (change.table === 'calendar_events')    return this.ruleEvent(change)
    if (change.table === 'resources_calendar') return this.ruleSick(change)
    return []
  }

  // ── Rules ────────────────────────────────────────────────────────────────────

  private async ruleBooking (change: SyncChange): Promise<GeneratedItem[]> {
    const items: GeneratedItem[] = []
    const { after, before } = change
    const client   = after.clientname || after.clientid || 'Unbekannt'
    const eventId  = after.calid || after.eventid || after.calendareventid
    const evTitle  = after.event_title  as string | undefined
    const evStart  = after.event_start  as string | undefined
    const paxLeft  = after.participants_remaining as number | undefined

    // Absage heisst 'cancelledbyclient' oder 'cancelledbycompany' — der frueher
    // hier gepruefte Altwert 'cancelled' wird von keiner Oberflaeche mehr
    // geschrieben, die Regel feuerte damit praktisch nie.
    const cancelled =
      (change.type === 'updated' && isCancelledStatus(after.status) && !isCancelledStatus(before?.status)) ||
      change.type === 'deleted'

    if (cancelled) {
      const desc = evTitle
        ? `${client} hat storniert.${paxLeft != null ? ` Verbleibend: ${paxLeft} Teilnehmer.` : ''}`
        : `${client} hat die Buchung storniert.`

      items.push(this.make('booking_cancelled', 2, {
        title:                 `Stornierung: ${client}`,
        description:           desc,
        next_action:           'Warteliste prüfen oder Kunden kontaktieren',
        related_id:            after.id,
        related_type:          'calendar_booking',
        event_title:           evTitle,
        event_start:           evStart,
        participants_remaining: paxLeft
      }))

      // If explicitly 0 remaining passed in, or countBookings confirms empty
      const isKnownEmpty = paxLeft === 0
      if (eventId && (isKnownEmpty || await this.countBookings(eventId) === 0)) {
        items.push(this.make('empty_event', 1, {
          title:                 '⚠️ Kurs hat keine Teilnehmer mehr!',
          description:           `Nach Stornierung von ${client} ist ${evTitle || 'der Kurs'} leer.`,
          next_action:           'Kurs absagen oder bewerben',
          related_id:            eventId,
          related_type:          'calendar_event',
          event_title:           evTitle,
          participants_remaining: 0
        }))
      }
    }

    if (change.type === 'created' && !isCancelledStatus(after.status)) {
      items.push(this.make('booking_new', 3, {
        title: `Neue Buchung: ${client}`,
        related_id: after.id, related_type: 'calendar_booking'
      }))
    }

    return items
  }

  private ruleEvent (change: SyncChange): GeneratedItem[] {
    if (change.type !== 'updated' || !change.before) return []
    const { before: b, after: a } = change
    const changed = b.title !== a.title || b.datestart !== a.datestart ||
                    b.dateend !== a.dateend || b.trainerid !== a.trainerid
    if (!changed) return []
    return [this.make('event_modified', 2, {
      title:        `Kursänderung: ${a.title || 'Kurs'}`,
      description:  'Zeit, Ort oder Trainer wurde geändert.',
      next_action:  'Teilnehmer benachrichtigen',
      related_id:   a.id, related_type: 'calendar_event'
    })]
  }

  private async ruleSick (change: SyncChange): Promise<GeneratedItem[]> {
    if (change.type !== 'created' || change.after.type !== 'sickreport') return []
    const after  = change.after
    const staff  = after.resourcename || 'Mitarbeiter'
    const day    = (after.datestart || after.date || '').slice(0, 10)

    const items: GeneratedItem[] = [
      this.make('sick_report', 1, {
        title:       `🤒 Krankmeldung: ${staff}`,
        description: `${staff} hat sich krank gemeldet.`,
        next_action: 'Vertretung organisieren',
        related_id: after.id, related_type: 'resource'
      })
    ]

    if (after.resourceid && day) {
      const conflicts = await this.eventsForStaff(after.resourceid, day)
      for (const ev of conflicts) {
        items.push(this.make('coverage_gap', 1, {
          title:        `🚨 Trainerausfall: ${staff} → ${ev.title || 'Kurs'}`,
          description:  `${staff} ist krank, hat aber ${ev.title || ev.id} heute.`,
          next_action:  'Vertretung oder Absage sofort klären',
          related_id:   ev.id, related_type: 'calendar_event'
        }))
      }
    }

    return items
  }

  // ── Dexie helpers ────────────────────────────────────────────────────────────

  private async countBookings (eventId: string): Promise<number> {
    try {
      return await this.db.calendar_bookings.filter((b: any) =>
        (b.calid === eventId || b.eventid === eventId || b.calendareventid === eventId) &&
        isAttendeeStatus(b.status) && b.archive !== 1
      ).count()
    } catch { return 1 }
  }

  private async eventsForStaff (resourceId: string, date: string): Promise<any[]> {
    try {
      return await this.db.calendar_events.filter((ev: any) =>
        (ev.trainerid === resourceId || ev.resourceid === resourceId) &&
        (ev.datestart || '').slice(0, 10) === date && ev.archive !== 1
      ).toArray()
    } catch { return [] }
  }

  // ── Factory ──────────────────────────────────────────────────────────────────

  private make (
    type: string,
    priority: 1 | 2 | 3 | 4,
    opts: Partial<ActionOptions>
  ): GeneratedItem {
    return { type, options: { priority, status: 'open', title: '', ...opts } }
  }
}
